import PropTypes from 'prop-types';
import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

// Approximate, read-only preview of the customer-facing allocation-instructions
// email. Mirrors the server-side template's content (domain
// quota_instructions_email_template): greeting, product table, and the Allocate
// button. It is not the exact rendered HTML - the email is composed and sent by
// the backend - so styling may differ; the fields shown match what the customer
// receives.
export const EMAIL_SUBJECT =
  'Your Planet products are ready - allocate your quota';

const EMAIL_BODY =
  'The following products have been added to your account. To start using ' +
  'them, allocate their quota to a workspace from your Quota Management page.';

const AllocationInstructionsEmailPreview = ({caName, products, loading}) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      Subject
    </Typography>
    <Typography variant="body2" sx={{fontWeight: 600, mb: 1}}>
      {EMAIL_SUBJECT}
    </Typography>

    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'grey.50',
        p: 2,
      }}
    >
      <Typography variant="subtitle1" sx={{fontWeight: 700, mb: 1}}>
        Your products are ready to use
      </Typography>
      <Typography variant="body2" sx={{mb: 1}}>
        Hi {caName || 'there'} team,
      </Typography>
      <Typography variant="body2" sx={{mb: 2}}>
        {EMAIL_BODY}
      </Typography>

      {loading ? (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, py: 1}}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading products…
          </Typography>
        </Box>
      ) : products.length > 0 ? (
        <Table size="small" aria-label="Email product summary">
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Quota</TableCell>
              <TableCell>Term</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map(p => (
              <TableRow key={p.key}>
                <TableCell>{p.name}</TableCell>
                <TableCell>{p.quota}</TableCell>
                <TableCell>{p.term}</TableCell>
                <TableCell>{p.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No CA-level products to summarize. The email can&apos;t be sent until
          this account has a provisioned product.
        </Typography>
      )}

      <Divider sx={{my: 2}} />
      <Button
        variant="contained"
        size="small"
        disableRipple
        tabIndex={-1}
        sx={{pointerEvents: 'none'}}
      >
        Allocate your quota
      </Button>
    </Box>

    <Typography
      variant="caption"
      color="text.secondary"
      sx={{display: 'block', mt: 1}}
    >
      Approximate preview. The email is composed and sent by Planet; exact
      styling may differ.
    </Typography>
  </Box>
);

AllocationInstructionsEmailPreview.propTypes = {
  caName: PropTypes.string,
  loading: PropTypes.bool,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      quota: PropTypes.string.isRequired,
      term: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default AllocationInstructionsEmailPreview;
